import type { Language } from "./index";

// Keyed translation tables + navigation labels, split out of index.tsx so the
// barrel (index.tsx) holds only translation machinery (Language provider,
// t()/useT(), lookup helpers) while this file holds only translation data.

// ── Keyed translations ────────────────────────────────────────────
// 平台 Shell 字符串（原 src/i18n/platform-copy.ts）
export const platformCopy = {
  zh: {
    brandTitle: "统计学习工作室",
    brandSubtitle: "统计可视化学习",
    navLabel: "选择可视化模块",
    tipTitle: "探索。模拟。理解。",
    tipBody: "用交互式可视化工具建立统计直觉与推断信心。",
    documentSuffix: "统计学习可视化平台",
    resizeLabel: "调整左侧目录和模块内容宽度",
    groups: {
      "Core Visualizers": "核心可视化",
      "WALS Simulation": "模拟模块",
      "WALS MES": "方法模块",
    },
  },
  en: {
    brandTitle: "Statistics Learning Studio",
    brandSubtitle: "Statistics visualization learning",
    navLabel: "Choose visualization module",
    tipTitle: "Explore. Simulate. Understand.",
    tipBody: "Interactive visual tools to build statistical intuition and confidence.",
    documentSuffix: "Statistics Learning Visualization Platform",
    resizeLabel: "Resize sidebar and module content",
    groups: {
      "Core Visualizers": "Core Visualizers",
      "WALS Simulation": "Simulation",
      "WALS MES": "Methods",
    },
  },
} as const;

// 各 app 的导航标签 [label, pageTitle]
export const visualizerLabels: Record<string, Record<Language, [string, string]>> = {
  "confidence-interval": {
    zh: ["置信区间", "置信区间可视化"],
    en: ["Confidence Interval", "Confidence Interval Visualizer"],
  },
  "type-error": {
    zh: ["一类/二类错误", "一类/二类错误可视化"],
    en: ["Type I / II Error", "Type I / II Error Visualizer"],
  },
  regression: {
    zh: ["回归分析", "回归可视化"],
    en: ["Regression", "Regression Visualizer"],
  },
  "simulation-introduction": {
    zh: ["模拟导论", "模拟导论"],
    en: ["Simulation Introduction", "Simulation Introduction"],
  },
  "simulation-random-variable": {
    zh: ["随机变量", "随机变量生成"],
    en: ["Random Variables", "Random Variable Generation"],
  },
  "simulation-clt": {
    zh: ["中心极限定理", "中心极限定理"],
    en: ["Central Limit Theorem", "Central Limit Theorem"],
  },
  "simulation-variance-reduction": {
    zh: ["方差缩减", "蒙特卡洛方差缩减"],
    en: ["Variance Reduction", "Monte Carlo Variance Reduction"],
  },
  "simulation-resampling": {
    zh: ["重抽样", "重抽样方法"],
    en: ["Resampling", "Resampling Methods"],
  },
  "simulation-mcmc": {
    zh: ["MCMC", "马尔可夫链蒙特卡洛"],
    en: ["MCMC", "Markov Chain Monte Carlo"],
  },
  "mes-anova": {
    zh: ["方差分析", "方差分析"],
    en: ["ANOVA", "Analysis of Variance"],
  },
  "mes-confidence-interval": {
    zh: ["MES 置信区间", "MES 置信区间"],
    en: ["MES Confidence Interval", "MES Confidence Interval"],
  },
  "mes-distributions": {
    zh: ["概率分布", "概率分布"],
    en: ["Distributions", "Distributions"],
  },
  "mes-linear-regression": {
    zh: ["MES 线性回归", "MES 线性回归"],
    en: ["MES Linear Regression", "MES Linear Regression"],
  },
};

// ── Confidence Interval 翻译（原 copy.ts，合并到统一模块）──────────
export const confidenceIntervalCopy = {
  zh: {
    coreVisualizer: "核心可视化",
    title: "置信区间",
    kicker: "通过重复抽样理解不确定性 ✦",
    description:
      "置信区间用样本数据为未知总体参数构造一个估计区间。重复抽样可以帮助我们观察这种方法有多常覆盖真实值。",
    modelOutput: "模型输出",
    chartTitle: "重复抽样中的置信区间",
    chartDescription: "每条水平区间来自一次随机样本。竖直虚线表示真实均值。",
    samples: "样本数：{count}",
    capturesTrueMean: "覆盖真实均值",
    missesTrueMean: "未覆盖真实均值",
    trueMean: "真实均值（μ = {mean}）",
    populationScale: "总体尺度",
    sampleIndex: "样本序号",
    observedCoverage: "观察覆盖率",
    observedCoverageNote: "已生成区间中包含真实均值的比例。",
    samplesDrawn: "已抽样本数",
    samplesDrawnNote: "样本越多，长期覆盖模式越容易观察。",
    averageIntervalWidth: "平均区间宽度",
    averageIntervalWidthNote: "置信水平更高或总体变异更大时，区间通常更宽。",
    zMultiplier: "Z 乘数",
    zMultiplierNote: "构造每个区间时使用的临界值。",
    parameters: "参数",
    generateOne: "生成 1 个样本",
    generateTwenty: "生成 20 个样本",
    reset: "重置",
    controlHint: "用重复抽样比较区间表现和目标置信水平。",
    conceptKeyIdea: "概念 + 核心思想",
    whatIsTitle: "什么是置信区间？",
    whatIsBody: "置信区间是利用样本数据为未知总体参数构造的区间估计。",
    coverageTitle: "覆盖率是长期行为",
    coverageBody:
      "置信水平描述的是这种方法在重复抽样中长期覆盖真实参数的比例，而不是某一个区间包含真实值的概率。",
    formula: "公式",
    estimate: "估计值",
    criticalValue: "临界值",
    se: "标准误",
    formulaNote: "当前临界值：{zValue}。图中显示的总体均值：{populationMean}。",
    howToReadThis: "如何阅读",
    currentInterpretation: "当前解读",
    emptyInterpretation: "先生成一个样本，让学生看到一个区间，再讨论重复抽样。",
    healthyInterpretation: "当前覆盖率达到或超过目标置信水平，长期行为看起来比较稳定。",
    lowCoverageInterpretation: "当前覆盖率低于目标置信水平，适合讨论随机性和有限样本带来的波动。",
    emptyPrompt: "生成样本后开始讨论覆盖率。",
    missPrompt: "当前有 {misses} 个区间没有覆盖真实均值。",
    learningNote: "学习提示",
    learningNoteBody: "尝试调整样本量和总体标准差，观察区间宽度与覆盖率如何变化。",
    controlsTitle: "置信区间参数",
    controlsIntro: "调整模拟输入，再重复生成样本，观察区间捕捉真实均值的频率。",
    sampleSize: "样本量",
    sampleSizeHint: "样本量越大，置信区间通常越窄。",
    populationSD: "总体标准差",
    populationSDHint: "变异越大，区间越宽，小样本下覆盖率也更不稳定。",
    confidenceLevel: "置信水平",
    confidenceLevelHint: "置信水平越高，覆盖真实均值的频率越高，但区间也会更宽。",
    confidenceInfo: "95% 置信水平表示：长期来看，用这种方法构造的区间大约 95% 会包含真实总体均值。",
    sigmaAssumption: "总体 σ 假设",
    sigmaKnown: "σ 已知（z 区间）",
    sigmaUnknown: "σ 未知（t 区间）",
    sigmaKnownHint: "使用已知的总体标准差构造 z 区间。",
    sigmaUnknownHint: "用样本标准差 s 估计 σ，构造 t 区间。",
    criticalMultiplier: "临界值",
  },
  en: {
    coreVisualizer: "Core Visualizer",
    title: "Confidence Interval",
    kicker: "Understand uncertainty through repeated sampling ✦",
    description:
      "Confidence intervals quantify uncertainty about a population parameter by constructing an interval from sample data. Repeated sampling shows how often our intervals capture the true value.",
    modelOutput: "Model output",
    chartTitle: "Confidence intervals across repeated samples",
    chartDescription:
      "Each horizontal interval comes from a different random sample. The vertical dashed line marks the true mean.",
    samples: "Samples: {count}",
    capturesTrueMean: "Captures true mean",
    missesTrueMean: "Misses true mean",
    trueMean: "True Mean (μ = {mean})",
    populationScale: "Population scale",
    sampleIndex: "Sample index",
    observedCoverage: "Observed Coverage",
    observedCoverageNote: "Share of generated intervals that contain the true mean.",
    samplesDrawn: "Samples Drawn",
    samplesDrawnNote: "More samples make the long-run pattern easier to see.",
    averageIntervalWidth: "Average Interval Width",
    averageIntervalWidthNote: "Higher confidence or more variability usually widens intervals.",
    zMultiplier: "Z Multiplier",
    zMultiplierNote: "Critical value used to construct each interval.",
    parameters: "Parameters",
    generateOne: "Generate 1 Sample",
    generateTwenty: "Generate 20 Samples",
    reset: "Reset",
    controlHint: "Use repeated samples to compare interval behavior with the target confidence level.",
    conceptKeyIdea: "Concept + key idea",
    whatIsTitle: "What is a Confidence Interval?",
    whatIsBody:
      "A confidence interval is an interval-built estimate for an unknown population parameter from sample data.",
    coverageTitle: "Coverage is Long-Run Behavior",
    coverageBody:
      "The confidence level describes how often this method captures the true parameter across repeated samples, not the probability for any one interval.",
    formula: "Formula",
    estimate: "estimate",
    criticalValue: "critical value",
    se: "SE",
    formulaNote: "Current critical value: {zValue}. Population mean shown in the chart: {populationMean}.",
    howToReadThis: "How to read this",
    currentInterpretation: "Current interpretation",
    emptyInterpretation:
      "Start by generating a sample so students can see one interval before talking about repeated sampling.",
    healthyInterpretation:
      "Coverage is currently at or above the target confidence level, so the long-run behavior is looking healthy.",
    lowCoverageInterpretation:
      "Coverage is currently below the target confidence level, which is a good prompt to discuss randomness and finite samples.",
    emptyPrompt: "Generate samples to start the coverage conversation.",
    missPrompt: "{misses} interval(s) currently miss the true mean.",
    learningNote: "Learning note",
    learningNoteBody:
      "Try changing the sample size and population SD to see how interval width and coverage behave in practice.",
    controlsTitle: "Confidence Interval Controls",
    controlsIntro:
      "Tune the simulation inputs, then generate repeated samples to see how often intervals capture the true mean.",
    sampleSize: "Sample Size",
    sampleSizeHint: "Larger samples usually narrow the confidence interval.",
    populationSD: "Population SD",
    populationSDHint: "Higher variability makes intervals wider and coverage noisier in small samples.",
    confidenceLevel: "Confidence Level",
    confidenceLevelHint: "Higher confidence captures the true mean more often, but also widens the interval.",
    confidenceInfo:
      "A 95% confidence level means that over the long run, about 95% of intervals constructed this way will contain the true population mean.",
    sigmaAssumption: "Population sigma assumption",
    sigmaKnown: "Sigma known (z interval)",
    sigmaUnknown: "Sigma unknown (t interval)",
    sigmaKnownHint: "Use the known population SD to build a z interval.",
    sigmaUnknownHint: "Estimate sigma with the sample SD s to build a t interval.",
    criticalMultiplier: "Critical value",
  },
} as const;

// ── WALS UI chrome 翻译（keyed；WalsApp 直接使用 walsCopy）──
export const walsCopy = {
  zh: {
    modelOutput: "模型输出",
    parameters: "参数",
    conceptKeyIdea: "概念 + 核心思想",
    formula: "公式",
    howToReadThis: "如何阅读",
    dataTable: "数据表",
    run: "运行",
    addOneSample: "+1 个样本",
    addTwentySamples: "+20 个样本",
    formulaHelper: "以公式为锚点，把每个符号和参数控件、图表联系起来。",
    estimate: "估计值",
    criticalValue: "临界值",
    simulationResult: "模拟结果",
    simulationResultFormula: "f(参数, 随机种子)",
    redraw: "重新绘制",
    draw1Sample: "抽取 1 个样本",
    draw20Samples: "抽取 20 个样本",
  },
  en: {
    modelOutput: "Model output",
    parameters: "Parameters",
    conceptKeyIdea: "Concept + key idea",
    formula: "Formula",
    howToReadThis: "How to read this",
    dataTable: "Data table",
    run: "Run",
    addOneSample: "+1 Sample",
    addTwentySamples: "+20 Samples",
    formulaHelper: "Use the formula as the anchor, then connect each symbol back to the controls and chart.",
    estimate: "estimate",
    criticalValue: "critical value",
    simulationResult: "simulation result",
    simulationResultFormula: "f(parameters, random seed)",
    redraw: "Redraw",
    draw1Sample: "Draw 1 Sample",
    draw20Samples: "Draw 20 Samples",
  },
} as const;

// ── Type Error 翻译 ───────────────────────────────────────────────
export const typeErrorCopy = {
  zh: {
    coreVisualizer: "核心可视化",
    title: "一类/二类错误",
    description: "调整 α、移动真实均值，观察拒绝域、β 和检验功效如何一起变化。",
    modelOutput: "模型输出",
    chartTitle: "决策区域与重叠分布",
    chartDescription: "蓝色曲线表示原假设分布，绿色曲线表示真实分布，阴影区域表示两类错误。",
    testStatistic: "检验统计量",
    density: "密度",
    legend: "图例",
    nullDistribution: "原假设分布",
    trueDistribution: "真实分布",
    criticalBoundary: "临界边界",
    typeIErrorArea: "一类错误区域",
    typeIIErrorArea: "二类错误区域",
    alpha: "α",
    alphaNote: "当 H0 为真时错误拒绝它的概率。",
    betaLabel: "β",
    betaNote: "存在真实效应时没有检出的概率。",
    power: "检验功效",
    powerNote: "正确检出真实效应的概率。",
    effectSize: "效应大小",
    effectSizeNote: "真实均值与原假设均值之间的距离。",
    parameters: "参数",
    hypothesis: "假设形式",
    oneSided: "单边",
    twoSided: "双边",
    controlPanel: "控制面板",
    controlIntro: "调整决策规则和效应大小，观察阴影错误区域如何变化。",
    alphaLabel: "显著性水平 α",
    alphaHint: "设定假阳性率，并控制拒绝域的位置。",
    nullMeanLabel: "原假设均值 μ₀",
    nullMeanHint: "定义原假设分布的中心。",
    trueMeanLabel: "真实均值 μ₁",
    trueMeanHint: "移动真实分布，并改变效应大小。",
    stdDevLabel: "标准差 σ",
    stdDevHint: "分布越宽，重叠越多，通常会提高 β。",
    conceptKeyIdea: "概念 + 核心思想",
    twoKindsOfError: "两类错误",
    twoKindsOfErrorBody: "一类错误是拒绝了真实的原假设；二类错误是在真实效应存在时没有拒绝原假设。",
    alphaPowerTradeOff: "α 与检验功效的权衡",
    strongPower: "当前设置有较强检验功效，因此较可能检出真实效应。",
    moderatePower: "当前设置有中等检验功效，可讨论 α、离散程度和效应大小如何共同影响结果。",
    lowPower: "当前设置检验功效较低，即使存在真实效应也容易发生二类错误。",
    closeTrueMean: "真实均值仍接近原假设均值，所以两条曲线重叠很多。",
    strictAlpha: "更严格的 α 可以减少误报，但也会扩大接受域并提高 β。",
    largerAlpha: "在这里更大的 α 会降低 β，但一类错误区域也会扩大。",
    formula: "公式",
    howToReadThis: "如何阅读",
    currentHypotheses: "当前假设",
    currentTest: "当前检验：{testType}。临界值：{criticalValue}。",
    oneSidedTest: "单边检验",
    twoSidedTest: "双边检验",
    and: "和",
  },
  en: {
    coreVisualizer: "Core Visualizer",
    title: "Type I / II Error",
    description: "Adjust alpha, move the true mean, and watch rejection regions, beta, and power update together.",
    modelOutput: "Model output",
    chartTitle: "Decision regions and overlapping distributions",
    chartDescription: "The blue curve represents the null hypothesis, the red curve the true distribution, and the shaded regions show the two kinds of error.",
    testStatistic: "Test Statistic",
    density: "Density",
    legend: "Legend",
    nullDistribution: "Null distribution",
    trueDistribution: "True distribution",
    criticalBoundary: "Critical boundary",
    typeIErrorArea: "Type I error area",
    typeIIErrorArea: "Type II error area",
    alpha: "Alpha",
    alphaNote: "Probability of rejecting H0 when H0 is true.",
    betaLabel: "Beta",
    betaNote: "Probability of missing a real effect.",
    power: "Power",
    powerNote: "Probability of correctly detecting the effect.",
    effectSize: "Effect Size",
    effectSizeNote: "Distance between true mean and null mean.",
    parameters: "Parameters",
    hypothesis: "Hypothesis",
    oneSided: "One-sided",
    twoSided: "Two-sided",
    controlPanel: "Control Panel",
    controlIntro: "Change the decision rule and effect size to see how the shaded error regions respond.",
    alphaLabel: "Alpha (α)",
    alphaHint: "Sets the false-positive rate and controls the rejection region.",
    nullMeanLabel: "Null Mean (μ₀)",
    nullMeanHint: "Defines the center of the null distribution.",
    trueMeanLabel: "True Mean (μ₁)",
    trueMeanHint: "Moves the true distribution and changes the effect size.",
    stdDevLabel: "Standard Deviation (σ)",
    stdDevHint: "Wider distributions increase overlap and usually raise beta.",
    conceptKeyIdea: "Concept + key idea",
    twoKindsOfError: "Two kinds of error",
    twoKindsOfErrorBody: "A Type I error rejects a true null hypothesis. A Type II error fails to reject the null when a real effect exists.",
    alphaPowerTradeOff: "Alpha and power trade off",
    strongPower: "This setting has strong power, so the test is fairly likely to detect the true effect.",
    moderatePower: "This setting has moderate power. Students can discuss how alpha, spread, and effect size interact.",
    lowPower: "This setting has low power, which makes Type II errors common even when a real effect exists.",
    closeTrueMean: "The true mean is still close to the null mean, so the two curves overlap heavily.",
    strictAlpha: "A strict alpha protects against false positives, but it can widen the acceptance region and raise beta.",
    largerAlpha: "A larger alpha lowers beta here, but the blue Type I region also expands.",
    formula: "Formula",
    howToReadThis: "How to read this",
    currentHypotheses: "Current hypotheses",
    currentTest: "Current test: {testType}. Critical value: {criticalValue}.",
    oneSidedTest: "One-sided test",
    twoSidedTest: "Two-sided test",
    and: "and",
  },
} as const;

// ── Regression 翻译 ───────────────────────────────────────────────
export const regressionCopy = {
  zh: {
    coreVisualizer: "核心可视化",
    title: "回归分析",
    description: "选择数据集，手动画一条直线，并与最小二乘回归拟合进行比较。",
    modelOutput: "模型输出",
    chartTitle: "散点图与拟合线",
    chartDescription: "在图上点击并拖动来绘制自定义直线，然后与回归线比较。",
    explanatoryVariable: "解释变量",
    response: "响应变量",
    statistics: "统计量",
    sse: "残差平方和 (SSE)",
    lineType: "直线类型",
    regressionLine: "回归线",
    customLine: "自定义直线",
    noLine: "无线条",
    hover: "悬停",
    res: "残差",
    yHat: "ŷ",
    hoverOverAPoint: "悬停到一个点",
    parameters: "参数",
    controlPanel: "控制面板",
    controlIntro: "选择数据集，显示回归线；想重新尝试时可以清除手绘直线。",
    dataset: "数据集",
    datasetHint: "切换学生用来推理的散点图数据。",
    selectDataset: "选择数据集",
    regressionLineLabel: "回归线",
    regressionLineHint: "将模型拟合与自定义直线进行比较。",
    outliersRemoved: "移除异常值",
    outliersHint: "比较排除有影响观测值后的拟合效果。",
    clearCustomLine: "清除自定义直线",
    selectedDataset: "当前数据集",
    dataPoints: "数据点",
    outliers: "异常值",
    included: "包含",
    removed: "已移除",
    source: "来源",
    xVariable: "X 变量",
    yVariable: "Y 变量",
    conceptKeyIdea: "概念 + 核心思想",
    leastSquaresRegression: "最小二乘回归",
    regressionBody: "回归用一条直线描述解释变量与响应变量之间的关系。",
    residualsExplainFit: "残差解释拟合",
    residualsBody: "最小二乘直线会使整个数据集的残差平方和最小。",
    formula: "公式",
    formulaNote: "通过观察 SSE 如何变化，比较自定义直线和回归线。",
    teachingNotes: "教学提示",
    classroomFocus: "课堂重点",
    classroomFocusBody: "先让学生预测斜率并画出自己的直线，再用残差解释为什么某条线拟合得更好。",
  },
  en: {
    coreVisualizer: "Core Visualizer",
    title: "Regression",
    description: "Choose a dataset, draw a custom line, and compare it with the least-squares regression fit.",
    modelOutput: "Model output",
    chartTitle: "Scatterplot and fitted lines",
    chartDescription: "Click and drag on the graph to draw a custom line, then compare it with the regression line.",
    explanatoryVariable: "Explanatory variable",
    response: "Response",
    statistics: "Statistics",
    sse: "Sum of Squared Errors (SSE)",
    lineType: "Line Type",
    regressionLine: "Regression Line",
    customLine: "Custom Line",
    noLine: "No Line",
    hover: "Hover",
    res: "Res",
    yHat: "Y hat",
    hoverOverAPoint: "Hover over a point",
    parameters: "Parameters",
    controlPanel: "Control Panel",
    controlIntro: "Choose a dataset, reveal the regression line, and clear your hand-drawn line when you want to try again.",
    dataset: "Dataset",
    datasetHint: "Switch the scatterplot students are reasoning from.",
    selectDataset: "Select Dataset",
    regressionLineLabel: "Regression line",
    regressionLineHint: "Compare the model fit with a custom line.",
    outliersRemoved: "Outliers removed",
    outliersHint: "Compare the fit with influential observations excluded.",
    clearCustomLine: "Clear Custom Line",
    selectedDataset: "Selected dataset",
    dataPoints: "Data points",
    outliers: "Outliers",
    included: "Included",
    removed: "Removed",
    source: "Source",
    xVariable: "X variable",
    yVariable: "Y variable",
    conceptKeyIdea: "Concept + key idea",
    leastSquaresRegression: "Least-squares regression",
    regressionBody: "Regression uses a line to describe the relationship between an explanatory variable and a response variable.",
    residualsExplainFit: "Residuals explain fit",
    residualsBody: "The least-squares line is the line that minimizes the sum of squared residuals across the dataset.",
    formula: "Formula",
    formulaNote: "Compare the custom line and regression line by watching how SSE changes.",
    teachingNotes: "Teaching notes",
    classroomFocus: "Classroom focus",
    classroomFocusBody: "Ask students to predict the slope first, draw their line, and then use residuals to explain why one line fits better.",
  },
} as const;

// ── 合并所有 keyed translations ───────────────────────────────────
export const allTranslations = {
  zh: {
    platform: platformCopy.zh,
    confidenceInterval: confidenceIntervalCopy.zh,
    wals: walsCopy.zh,
    typeError: typeErrorCopy.zh,
    regression: regressionCopy.zh,
  },
  en: {
    platform: platformCopy.en,
    confidenceInterval: confidenceIntervalCopy.en,
    wals: walsCopy.en,
    typeError: typeErrorCopy.en,
    regression: regressionCopy.en,
  },
};
